import './Withdraw.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { Button, Form, FormItem, Header, Input, Placeholder } from '$uikit';
import { balanceActions } from '$store/balance';
import { roundNumber } from '$utils';
import { toastActions } from '$store/toast';
import { IcSuccess72 } from '$assets';
import { goBack } from '$navigation/helper';
import { useTranslator } from '$hooks';
import { formatInputAmount } from '$utils/number';
import { agentSelector } from '$store/agent';
import { mainSelector } from '$store/main';
import router from '$navigation/router';
import { OTHER } from '$shared/constants/pages';

export const Withdraw: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();
  const { balance, canWithdraw } = useSelector(mainSelector);
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [address, setAddress] = useState('');
  const [isSent, setSent] = useState(false);
  const agentId = +(route.params.aid || 0);
  const { balance: agentBalance } = useSelector(agentSelector);

  const avail = useMemo(() => {
    if (agentId > 0) {
      return agentBalance;
    } else {
      return balance;
    }
  }, [agentId, balance, agentBalance]);

  const handleAmountChange = useCallback(({ target }) => {
    setAmount(formatInputAmount(target.value));
  }, []);

  const handleAddressChange = useCallback(({ target }) => {
    setAddress(target.value);
  }, []);

  const handleSend = useCallback(() => {
    const amountPrepared = new BigNumber(amount).toNumber();
    if (amountPrepared <= 0) {
      return dispatch(toastActions.setToastFail(t('withdraw_amount_error')));
    }

    const addressPrepared = address.trim();
    if (addressPrepared.length < 32) {
      return dispatch(toastActions.setToastFail(t('withdraw_address_error')));
    }

    dispatch(
      balanceActions.withdraw({
        amount: amountPrepared,
        address: addressPrepared,
        onDone: () => setSent(true),
        agentId,
        comment: comment.trim(),
      }),
    );
  }, [dispatch, amount, address, t, agentId, comment]);

  useEffect(() => {
    if (!canWithdraw) {
      dispatch(toastActions.setToastFail(t('balance_withdraw_not_allowed')));
      goBack();
    }
  }, [canWithdraw]);

  const handleBack = () => {
    router.navigate(OTHER, { w: 'balance' });
  };

  const amountWithFee = useMemo(() => {
    return +amount > 0 ? new BigNumber(amount).plus(0.02).toNumber() : 0;
  }, [amount]);

  if (!canWithdraw) {
    return null;
  }

  function renderContent() {
    if (isSent) {
      return (
        <Placeholder
          title={t('withdraw_success_title')}
          icon={<IcSuccess72 />}
          centered
          actions={<Button onClick={handleBack}>{t('withdraw_success_button')}</Button>}
        >
          {agentId > 0 ? null : t('withdraw_success_caption')}
        </Placeholder>
      );
    } else {
      return (
        <Form>
          <FormItem
            title={t('withdraw_amount')}
            description={t('withdraw_amount_description', {
              balance: roundNumber(avail, 6),
              fee: 0.02,
            })}
          >
            <Input
              placeholder={t('withdraw_amount_placeholder')}
              type="number"
              value={amount}
              onChange={handleAmountChange}
            />
          </FormItem>
          <FormItem
            title={t('withdraw_comment')}
            description={t('withdraw_comment_description')}
          >
            <Input
              placeholder={t('withdraw_comment_placeholder')}
              value={comment}
              onChange={({ target }) => setComment(target.value)}
              maxLength={150}
            />
          </FormItem>
          <FormItem
            title={t('withdraw_address')}
            description={t('withdraw_address_description')}
          >
            <Input
              placeholder={t('withdraw_address_placeholder')}
              value={address}
              onChange={handleAddressChange}
              multiline
            />
          </FormItem>
          <FormItem>
            <Button onClick={handleSend}>
              {t('withdraw_button', { amount: amountWithFee })}
            </Button>
          </FormItem>
          <FormItem>
            <Button
              mode="secondary"
              href="https://t.me/CryptoBot?start=r-1325-market"
              target="_blank"
            >
              {t('sell_ton_btn')}
            </Button>
          </FormItem>
        </Form>
      );
    }
  }

  return (
    <>
      <Header showCloseButton>{t('withdraw_title')}</Header>
      {renderContent()}
    </>
  );
};
